import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OurNode } from "@src/cloud-connect/poc/our-node";

import { throttle } from "@src/cloud-connect/utils/throttle";
import styles from "../TreeComponents.module.scss";
import { TreeOurNode } from "./tree-our-node";
import { useTreeOurNodeInitialHook } from "./use-tree-our-node-initial-hook";
import { useTreeOurNodeMachineHook } from "./use-tree-our-node-machine-hook";
import { removeNotVisibleNodesFromFlatList } from "./use-cases/remove-not-visible-nodes-from-flat-list";
import {
  updateActiveInitialStructureNode,
  updateActiveMachineStructureNode,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
type TreeViewProps = {
  data: OurNode[];
  type: "initial" | "machine";
};

const NODE_RENDER_THRESHOLD = 20;
const NODE_HEIGHT = 40;
const PADDING_LEFT_PER_LEVEL = 28;
const OUTLINE_CORRECTION_VALUE = 1;
const DEFAULT_LIST_START = 0;
const DEFAULT_LIST_END = 1;

export const TreeViewArrayFromMap = ({ data, type }: TreeViewProps) => {
  const listElementRef = useRef<HTMLUListElement | null>(null);
  const activeNodeId = useSelector((state: RootState) =>
    type === "initial"
      ? state.yourSlice.activeInitialStructureNode
      : state.yourSlice.activeMachineStructureNode,
  );
  const dispatch = useDispatch();
  // Capture the active node only when this tree view mounts. When navigating
  // from the filtered view, this component mounts with the selected node and
  // should scroll to it. Selecting nodes or changing checkboxes afterward
  // must not create another scroll request.
  const nodeToScrollTo = useRef<string | null>(activeNodeId || null);

  const [listSegmnentToRender, setListSegmentToRender] = useState({
    start: DEFAULT_LIST_START,
    end: DEFAULT_LIST_END,
  });

  const [localFlatList, setLocalFlatList] = useState<OurNode[]>([]);

  /**
   * Counts the number of levels in the parent chain of the given object.
   * @param {object} obj - The input object whose parent chain will be traversed to count recursion levels.
   * @returns {number} - The number of levels in the parent chain.
   */
  const countParentRecursionLevels = (obj) => {
    let count = 0; // Initialize the count of parent recursion levels to zero

    // Continue looping until the 'parent' property of the object is undefined
    // if (!obj) {
    //   return count; // If the object is null or undefined, return zero
    // }

    while (obj !== null && obj !== undefined && obj.parent !== null) {
      //while (obj.parent !== null) {
      count++; // Increment the count of parent recursion levels
      obj = obj.parent; // Move to the parent object
    }

    return count; // Return the total count of parent recursion levels
  };

  const calcListToRender = useCallback(() => {
    if (listElementRef.current && listElementRef.current.parentElement) {
      const containerHeight = listElementRef.current.parentElement.offsetHeight;
      const scrollTop = listElementRef.current.parentElement.scrollTop;
      const startIndex = Math.max(
        0,
        Math.floor(scrollTop / NODE_HEIGHT) - NODE_RENDER_THRESHOLD,
      );
      const endIndex = Math.min(
        localFlatList.length,
        Math.ceil(
          (scrollTop + containerHeight) / NODE_HEIGHT + NODE_RENDER_THRESHOLD,
        ),
      );
      setListSegmentToRender({
        start: startIndex,
        end: endIndex,
      });
    }
  }, [localFlatList]);

  useEffect(() => {
    if (listElementRef.current) {
      listElementRef.current.style.height = `${
        localFlatList.length * NODE_HEIGHT
      }px`;
      listElementRef.current.style.position = "relative";
      listElementRef.current.style.margin = "0 0";
    }
    calcListToRender();
  }, [calcListToRender, localFlatList]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      calcListToRender();
    }, 100);

    if (listElementRef.current) {
      const parentElement = listElementRef.current.parentElement;
      parentElement?.addEventListener("scroll", handleScroll);
      return () => {
        parentElement?.removeEventListener("scroll", handleScroll);
      };
    }
  }, [calcListToRender]);

  useEffect(() => {
    const listWithRemovedNodes = removeNotVisibleNodesFromFlatList(data);
    setLocalFlatList(listWithRemovedNodes);
  }, [data]);

  // Scroll to clicked node from filtered view.
  useEffect(() => {
    if (!nodeToScrollTo.current) return;
    if (localFlatList.length === 0) return;

    const index = localFlatList.findIndex(
      (n) => n.key === nodeToScrollTo.current,
    );
    if (index === -1) return;

    const container = listElementRef.current?.parentElement;
    if (!container) return;

    container.scrollTo({
      top: index * NODE_HEIGHT - container.offsetHeight / 2 + NODE_HEIGHT / 2,
      behavior: "smooth",
    });

    nodeToScrollTo.current = null;
  }, [localFlatList]);

  // Reset Redux selected node on unmount. This is needed to prevent unwanted scroll when the component is remounted with a previously selected node.
  useEffect(() => {
    return () => {
      dispatch(updateActiveInitialStructureNode(""));
      dispatch(updateActiveMachineStructureNode(""));
    };
  }, [type, dispatch]);

  return (
    <ul ref={listElementRef} className={styles["tree-list"]}>
      {localFlatList &&
        localFlatList
          .slice(listSegmnentToRender.start, listSegmnentToRender.end)
          .map((item, i) => {
            return (
              <span
                key={item.key}
                style={{
                  position: "absolute",
                  top: NODE_HEIGHT * (listSegmnentToRender.start + i),
                  left:
                    PADDING_LEFT_PER_LEVEL * countParentRecursionLevels(item),
                  width: `calc(100% - ${
                    PADDING_LEFT_PER_LEVEL * countParentRecursionLevels(item) +
                    OUTLINE_CORRECTION_VALUE
                  }px)`,
                }}
              >
                <TreeOurNode
                  child={item}
                  type={type}
                  list={data}
                  useHook={
                    type === "initial"
                      ? useTreeOurNodeInitialHook
                      : useTreeOurNodeMachineHook
                  }
                />
              </span>
            );
          })}
    </ul>
  );
};
